'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { apiGet } from '../../lib/apiClient'
import { PROOF_LIMIT, proofCard } from '../../lib/proofCard'
import { trackPointer, resetPointer } from '../motion3d/pointer'
import './home-v2.css'

const safeSlug = (slug) => encodeURIComponent(String(slug))

// First paint uses the case studies app/page.tsx hands down (build-time
// snapshot plus the static flagship studies), so the cards and their links
// are in the server HTML. The live API refreshes them after hydration and
// never blanks them. It is called directly rather than through
// lib/caseStudiesApi, which would bundle the whole case-study snapshot into
// the homepage's JavaScript.
export default function ProofV2({ initialStudies = [] }) {
  const [studies, setStudies] = useState(() => initialStudies.slice(0, PROOF_LIMIT))

  useEffect(() => {
    let live = true
    apiGet('/api/case-studies').then(({ data }) => {
      if (live && Array.isArray(data) && data.length >= PROOF_LIMIT) {
        setStudies(data.slice(0, PROOF_LIMIT).map(proofCard))
      }
    })
    return () => {
      live = false
    }
  }, [])

  // Nothing to prove with an empty list -- the section stays out of the page
  // rather than rendering placeholder cards.
  if (!studies.length) return null

  return (
    <section className="hv2 hv2-section is-white">
      <div className="hv2-inner">
        <div className="hv2-head centered" data-rv="0.4">
          <span className="hv2-eyebrow">Proof of work</span>
          <h2>Systems Already <span className="hv2-hl">Running in Production.</span></h2>
          <p>Real builds for real agencies, the automations, AI agents, and CRMs behind them are live right now.</p>
        </div>

        <div className="hv2-proof">
          {studies.map((study, i) => (
            <div
              className="hv2-proof-slot"
              key={study.id || study.slug || i}
              // Arrives out of depth each time it scrolls back into view.
              data-rv="0.25"
              data-rv-fx="depth"
              data-rv-repeat=""
              style={{ '--rv-p': '1100px', '--rv-y': '20px', '--rv-z': '-280px', '--rv-dur': '.9s', '--rv-delay': `${i * 0.1}s` }}
            >
              <Link
                href={study.slug ? `/case-studies/${safeSlug(study.slug)}` : '/case-studies'}
                className="hv2-proof-card"
                onMouseMove={trackPointer}
                onMouseLeave={resetPointer}
              >
                {study.image ? (
                  <span className="hv2-proof-media">
                    <img src={study.image} alt={study.title || 'Case study'} loading="lazy" decoding="async" />
                  </span>
                ) : null}
                <span className="hv2-proof-body">
                  <span className="hv2-proof-tag">{study.category || 'Case study'}</span>
                  <h3>{study.title}</h3>
                  <p>{study.excerpt}</p>
                  <span className="hv2-proof-link">Read the build <ArrowRight size={16} /></span>
                </span>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
